import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LoaderOverlayComponent } from '../../components/loader-overlay/loader-overlay.component';
import AngularRoutingHelperService from '../../services/angular-routing-helper.service';
import DictionaryService from '../../services/dictionary.service';
import RoutingService from '../../services/routing.service';
import ToolkitService from '../../services/toolkit.service';
import { parseJsonLenient } from '../../utils/toolkit-json.util';

@Component({
	standalone: true,
	selector: 'toolkit-eek2-history-view',
	templateUrl: './eek2-history.component.html',
	styleUrl: './eek2-history.component.scss',
	imports: [CommonModule, LoaderOverlayComponent]
})
export default class ToolkitEek2HistoryComponent implements OnInit {
	private route: ActivatedRoute = inject(ActivatedRoute);
	private routingService: RoutingService = inject(RoutingService);
	private angularRoutingHelperService: AngularRoutingHelperService = inject(AngularRoutingHelperService);
	private toolkitService: ToolkitService = inject(ToolkitService);
	private dictionaryService: DictionaryService = inject(DictionaryService);

	vm: any = {
		loading: false,
		error: false,
		assignmentId: null,
		returnTo: null,
		assignment: null,
		assessment: null,
		rawQuestions: null,
		questions: null,
		historyAssignments: [],
		history: [],
		tableRows: [],
		trendCards: [],
		currentRecordId: null,
		dateText: ''
	};

	private readonly subscales = [
		{ code: 'DEP', key: 'depression', cutoff: 12, itemOrders: [1, 2, 3, 4, 5, 6, 7, 8] },
		{ code: 'YAR', key: 'anxiety', cutoff: 12, itemOrders: [9, 10, 11, 12, 13, 14] },
		{ code: 'PAF', key: 'panic', cutoff: 7, itemOrders: [15, 16, 17, 18, 19] },
		{ code: 'VAS', key: 'asthenia', cutoff: 8, itemOrders: [22, 23, 24, 25] },
		{ code: 'INS', key: 'insomnia', cutoff: 6, itemOrders: [26, 27, 28] }
	];

	ngOnInit() {
		this.routingService.validateAccess('myExercises').then(() => this.onResolve());
	}

	get isEt(): boolean {
		return (this.dictionaryService.getCurrentLocale() || 'en_GB').toString().toLowerCase().startsWith('et');
	}

	get copy() {
		return {
			title: this.tr('Toolkit.EEK2.History.Title', this.isEt ? 'Varasemad hindamised' : 'Past assessments'),
			empty: this.tr('Toolkit.EEK2.History.Empty', this.isEt ? 'Salvestatud hindamisi veel ei ole.' : 'No saved assessments yet.'),
			trendTitle: this.tr('Toolkit.EEK2.History.TrendTitle', this.isEt ? 'Trend alaskaala kaupa' : 'Trend by subscale'),
			viewing: this.tr('Toolkit.EEK2.History.Viewing', this.isEt ? 'Praegune tulemus' : 'Current result'),
			date: this.tr('Toolkit.EEK2.Therapist.Col.Date', this.isEt ? 'Kuupaev' : 'Date'),
			backToResults: this.tr('Toolkit.EEK2.History.BackToResults', this.isEt ? 'Tagasi tulemustesse' : 'Back to results'),
			cutoff: (c: number) => this.tr('Toolkit.EEK2.History.Trend.Cutoff', this.isEt ? 'Laevi {c}' : 'Cutoff {c}').replace('{c}', `${c}`),
			delta: (d: string) => this.tr('Toolkit.EEK2.History.Trend.Delta', this.isEt ? 'Esimesest viimaseni: {delta}' : 'First to latest: {delta}').replace('{delta}', d),
			flat: this.tr('Toolkit.EEK2.History.Trend.Flat', this.isEt ? 'stabiilne' : 'stable'),
			toward: this.tr('Toolkit.EEK2.History.Trend.Toward', this.isEt ? 'liigub laeve poole' : 'trending toward cutoff'),
			away: this.tr('Toolkit.EEK2.History.Trend.Away', this.isEt ? 'liigub laevest eemale' : 'trending away from cutoff')
		};
	}

	private onResolve() {
		this.vm.loading = true;
		this.vm.error = false;
		this.vm.assignmentId = (this.route.snapshot.paramMap.get('assignmentId') || '').toString();
		this.vm.returnTo = (this.route.snapshot.queryParamMap.get('return_to') || `/my-exercises/resource/survey/${this.vm.assignmentId}`).toString();

		if (!this.vm.assignmentId) {
			this.vm.loading = false;
			this.vm.error = true;
			return;
		}

		this.toolkitService.getMyAssessmentAssignment(
			{ id: this.vm.assignmentId },
			(assignment) => {
				this.vm.assignment = assignment || null;
				if (!assignment?.assessmentId) {
					this.vm.loading = false;
					this.vm.error = true;
					return;
				}
				this.loadAssessmentContext();
			},
			() => {
				this.vm.loading = false;
				this.vm.error = true;
			}
		);
	}

	private loadAssessmentContext() {
		let remaining = 2;
		let failed = false;
		const fail = () => {
			if (failed) return;
			failed = true;
			this.vm.loading = false;
			this.vm.error = true;
		};
		const done = () => {
			if (failed) return;
			remaining--;
			if (remaining > 0) return;
			this.buildViewModel();
		};

		this.toolkitService.getAssessment(
			{ id: this.vm.assignment.assessmentId },
			(assessment) => {
				this.vm.assessment = assessment || null;
				done();
			},
			fail
		);

		this.toolkitService.getMyAssessmentAssignmentsList(
			{},
			(assignments) => {
				this.vm.historyAssignments = Array.isArray(assignments) ? assignments : [];
				done();
			},
			fail
		);
	}

	private buildViewModel() {
		const assessment = this.vm.assessment || {};
		const qRaw = this.vm.assignment?.assessmentQuestionsSnapshotJson || assessment?.questionsJson || assessment?.questions_json || null;
		this.vm.rawQuestions = qRaw ? parseJsonLenient(qRaw) : null;
		this.vm.questions = this.normalizeSurveyQuestions(this.vm.rawQuestions);

		if (!this.isEek2Assessment(this.vm.rawQuestions, assessment)) {
			this.vm.loading = false;
			this.vm.error = true;
			return;
		}

		const items = this.sortedItems();
		const history = (Array.isArray(this.vm.historyAssignments) ? this.vm.historyAssignments : [])
			.filter((assignment: any) => this.matchesAssessment(assignment))
			.map((assignment: any) => this.toRecord(assignment, items))
			.filter((record: any) => !!record)
			.sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

		const current = this.toRecord(this.vm.assignment, items);
		if (current && !history.some((record: any) => record.id === current.id)) history.unshift(current);

		this.vm.history = history;
		this.vm.currentRecordId = current?.id || null;
		this.vm.dateText = current?.timestamp ? this.formatDate(current.timestamp) : '';
		this.vm.tableRows = history.map((record: any) => ({
			id: record.id,
			dateText: this.formatDate(record.timestamp),
			scores: this.subscales.map((sub) => ({
				code: sub.code,
				score: record.scores[sub.code]?.score ?? 0,
				above: !!record.scores[sub.code]?.above
			}))
		}));
		this.vm.trendCards = this.subscales.map((sub) => this.trendCard(history, sub));
		this.vm.loading = false;
	}

	backToResults() {
		this.angularRoutingHelperService.setPath(this.vm.returnTo || '/my-exercises', false, false);
	}

	private tr(key: string, fallback?: string): string {
		const translated = this.dictionaryService.translate(key);
		return translated && translated !== key ? translated : (fallback || key);
	}

	private normalizeSurveyQuestions(rawQuestions: any): any | null {
		if (!rawQuestions || typeof rawQuestions !== 'object') return null;
		if (Array.isArray(rawQuestions?.items)) return rawQuestions;

		const instrument = rawQuestions?.instrument;
		if (!instrument || !Array.isArray(instrument?.items)) return rawQuestions;

		return {
			...rawQuestions,
			items: instrument.items.slice().sort((a: any, b: any) => (a?.order || 0) - (b?.order || 0))
		};
	}

	private isEek2Assessment(questions: any, assessment: any): boolean {
		const instrument = questions?.instrument || null;
		const code = (instrument?.code || questions?.code || assessment?.code || assessment?.title || '').toString().trim().toLowerCase();
		const id = (instrument?.id || '').toString().trim().toLowerCase();
		return id === 'eek2' || code === 'eek-2' || code === 'eek2';
	}

	private sortedItems(): any[] {
		const instrument = this.vm.rawQuestions?.instrument || this.vm.questions || {};
		const items = Array.isArray(instrument?.items) ? instrument.items : [];
		return items.slice().sort((a: any, b: any) => (a?.order || 0) - (b?.order || 0));
	}

	private matchesAssessment(assignment: any): boolean {
		const assignmentAssessmentId = (assignment?.assessmentId ?? assignment?._assessment?.id ?? '').toString();
		const currentAssessmentId = (this.vm.assignment?.assessmentId ?? this.vm.assessment?.id ?? '').toString();
		return !!assignmentAssessmentId && !!currentAssessmentId && assignmentAssessmentId === currentAssessmentId;
	}

	private extractAnswers(assignment: any, itemCount: number): any[] {
		const raw = assignment?.answersJson || assignment?.answers_json || null;
		if (!raw) return [];
		try {
			const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
			return Array.isArray(parsed) ? parsed.slice(0, itemCount) : [];
		} catch {
			return [];
		}
	}

	private toRecord(assignment: any, items: any[]): any | null {
		if (!assignment) return null;
		const answers = this.extractAnswers(assignment, items.length);
		if (answers.length < items.length) return null;
		const responses: any = {};
		items.forEach((item: any, index: number) => {
			responses[item.order] = answers[index];
		});
		const timestamp = assignment?.completedAt || assignment?.updatedAt || assignment?.created || assignment?.assignedAt || new Date().toISOString();
		return {
			id: (assignment?.id ?? timestamp).toString(),
			timestamp,
			scores: this.computeScores(responses)
		};
	}

	private computeScores(responses: any): any {
		const out: any = {};
		this.subscales.forEach((sub) => {
			const score = sub.itemOrders.reduce((sum, order) => {
				const value = Number(responses[order]);
				return sum + (isFinite(value) ? value : 0);
			}, 0);
			out[sub.code] = {
				score,
				max: sub.itemOrders.length * 4,
				cutoff: sub.cutoff,
				above: score >= sub.cutoff
			};
		});
		return out;
	}

	private trendCard(history: any[], sub: any): any {
		const chrono = history.slice().reverse();
		const values = chrono.map((record) => record.scores[sub.code]?.score || 0);
		const first = values[0] ?? 0;
		const last = values[values.length - 1] ?? 0;
		const delta = last - first;
		const deltaStr = `${delta > 0 ? '+' : ''}${delta}`;
		const trendWord = values.length < 2 || delta === 0 ? this.copy.flat : delta > 0 ? this.copy.toward : this.copy.away;
		return {
			label: this.subscaleLabel(sub.key),
			code: sub.code,
			meta: `${this.copy.cutoff(sub.cutoff)} · ${this.copy.delta(deltaStr)} · ${trendWord}`,
			sparkline: this.sparkline(values, sub.itemOrders.length * 4, sub.cutoff)
		};
	}

	private sparkline(values: number[], max: number, cutoff: number) {
		const w = 200;
		const h = 56;
		const pad = 4;
		const innerW = w - pad * 2;
		const innerH = h - pad * 2;
		const x = (i: number) => values.length <= 1 ? pad + innerW / 2 : pad + (i / (values.length - 1)) * innerW;
		const y = (value: number) => pad + innerH - Math.max(0, Math.min(1, value / max)) * innerH;
		const points = values.map((value, index) => `${x(index).toFixed(1)},${y(value).toFixed(1)}`).join(' ');
		const cutoffY = y(cutoff).toFixed(1);
		const lastOver = (values[values.length - 1] || 0) >= cutoff;
		return {
			cutoffY,
			pathPoints: points,
			lineColor: lastOver ? 'var(--eek2-pink)' : 'var(--eek2-green)',
			dots: values.map((value, index) => ({
				x: x(index).toFixed(1),
				y: y(value).toFixed(1),
				color: value >= cutoff ? 'var(--eek2-pink)' : 'var(--eek2-green)'
			}))
		};
	}

	private subscaleLabel(key: string): string {
		return this.tr(`Toolkit.EEK2.Results.Subscale.${key}.Label`, key);
	}

	private formatDate(value: any): string {
		const date = new Date(value);
		if (!isFinite(date.getTime())) return '';
		return new Intl.DateTimeFormat(this.isEt ? 'et-EE' : 'en-GB', {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		}).format(date);
	}
}
